<template>
  <div class="tab-general md:ml-4 md:mt-0 mt-4 ml-0">
    <vx-card v-if="isPageLoading" class="mb-6">
      <div class="py-8 text-center text-grey">
        กำลังโหลดข้อมูล...
      </div>
    </vx-card>

    <template v-else>

    <vx-card class="mb-6">
      <vs-tabs v-model="activeTabIndex" position="top" class="tabs-shadow-none">
        <vs-tab label="กฎเลือกช่องทางฝาก (Payment Rules)">
          <div class="pt-4">
            <payment-rules-panel embedded />
          </div>
        </vs-tab>

        <vs-tab :label="'ยอดฝากขั้นต่ำ'">
          <div class="pt-4">
            <h4 class="mb-4">ยอดฝากขั้นต่ำ</h4>
          <vs-table stripe :data="bankLists">
            <template slot="thead">
              <vs-th>payment_name</vs-th>
              <vs-th>payment_type</vs-th>
              <vs-th>min_deposit</vs-th>
              <vs-th width="180">setting</vs-th>
            </template>
            <template slot-scope="{ data }">
              <vs-tr :key="item.id || item.code" v-for="item in data">
                <vs-td :data="item.name_th || item.name_en || item.code">
                  {{ item.name_th || item.name_en || item.code }}
                </vs-td>
                <vs-td :data="item.type">
                  {{ item.type }}
                </vs-td>
                <vs-td :data="item.min_deposit">
                  {{ formatNumber(item.min_deposit) }}
                </vs-td>
                <vs-td>
                  <div class="flex">
                    <vs-button
                      type="border"
                      size="small"
                      icon-pack="feather"
                      icon="icon-edit"
                      color="warning"
                      class="mr-2"
                      @click="openMinDepositModal(item)"
                    >
                      แก้ไข
                    </vs-button>
                  </div>
                </vs-td>
              </vs-tr>
            </template>
          </vs-table>
          <div v-if="bankLists.length === 0 && !loading" class="text-center py-8 text-grey">
            ยังไม่มีข้อมูล payment — ระบบยังไม่ส่งรายการช่องทางชำระเงินมา
          </div>
          </div>
        </vs-tab>
      </vs-tabs>
    </vx-card>

    <!-- Modal แก้ไข min_deposit -->
    <vs-popup
      title="แก้ไขยอดฝากขั้นต่ำ"
      :active.sync="showMinDepositModal"
      classContent="popup-example"
      @close="resetMinDepositForm"
    >
      <div class="vx-row">
        <div class="vx-col w-full mb-4">
          <p class="text-sm text-grey">
            {{ minDepositForm.name || minDepositForm.code }}
          </p>
        </div>
        <div class="vx-col w-full mb-4">
          <vs-input
            v-model.number="minDepositForm.min_deposit"
            type="number"
            label="min_deposit (บาท)"
            class="w-full"
            min="0"
            step="0.01"
          />
        </div>
      </div>
      <div class="flex justify-end mt-4">
        <vs-button type="border" @click="showMinDepositModal = false">ยกเลิก</vs-button>
        <vs-button class="ml-2" :disabled="savingMinDeposit" @click="submitMinDeposit">
          {{ savingMinDeposit ? 'กำลังบันทึก...' : 'บันทึก' }}
        </vs-button>
      </div>
    </vs-popup>

    <!-- Modal เพิ่ม/แก้ไข -->
    <vs-popup
      :title="formMode === 'add' ? 'เพิ่ม range ใหม่' : 'แก้ไข range'"
      :active.sync="showFormModal"
      classContent="popup-example"
      @close="resetForm"
    >
      <div class="vx-row">
        <div class="vx-col w-full mb-4">
          <vs-input
            v-model.number="form.min_amount"
            type="number"
            label="min_amount (บาท)"
            class="w-full"
            min="0"
            step="0.01"
          />
          <span v-if="formErrors.min_amount" class="text-danger text-sm">{{ formErrors.min_amount }}</span>
        </div>
        <div class="vx-col w-full mb-4">
          <vs-select
            autocomplete
            v-model="form.payment_code"
            label="payment_code"
            class="w-full"
            placeholder="เลือกช่องทางการชำระเงิน"
          >
            <vs-select-item
              v-for="(item, i) in bankLists"
              :key="item.id || i"
              :value="item.code"
              :text="item.name_th || item.name_en || item.code"
            />
          </vs-select>
          <span v-if="formErrors.payment_code" class="text-danger text-sm">{{ formErrors.payment_code }}</span>
        </div>
        <div class="vx-col w-full mb-4">
          <vs-checkbox v-model="form.is_active">is_active (เปิดใช้งาน)</vs-checkbox>
        </div>
      </div>
      <div class="flex justify-end mt-4">
        <vs-button type="border" @click="showFormModal = false">ยกเลิก</vs-button>
        <vs-button class="ml-2" :disabled="saving" @click="submitForm">
          {{ formMode === 'add' ? 'เพิ่ม' : 'บันทึก' }}
        </vs-button>
      </div>
    </vs-popup>

    <!-- Modal ยืนยันลบ -->
    <vs-popup
      title="ยืนยันการลบ"
      :active.sync="showDeleteModal"
      classContent="popup-example"
    >
      <p v-if="deleteTarget" class="mb-4">
        คุณต้องการลบ range <strong>{{ formatNumber(deleteTarget.min_amount) }} - {{ formatNumber(deleteTarget.max_amount) }}</strong> ({{ deleteTarget.payment_code }}) ใช่หรือไม่?
      </p>
      <div class="flex justify-end">
        <vs-button type="border" @click="showDeleteModal = false">ยกเลิก</vs-button>
        <vs-button color="danger" class="ml-2" :disabled="deleting" @click="confirmDelete">ยืนยันลบ</vs-button>
      </div>
    </vs-popup>
    </template>
  </div>
</template>

<script>
import {
  getRanges,
  getBankLists,
  createRange,
  updateRange,
  deleteRange,
  getBankPaymentMapping,
  saveBankPaymentMapping,
  getDepositRangesFilterStatus,
  updateDepositRangesFilterStatus,
  updatePaymentMinDeposit
} from '@/api/settingPayment'
import PaymentRulesPanel from './PaymentRulesPanel.vue'

// รายการธนาคารลูกค้า (รหัสธนาคาร) สำหรับจับคู่กับ payment
const BANK_OPTIONS = [
  { label: 'ไทยพาณิชย์ (SCB)', value: '000' },
  { label: 'กสิกรไทย (KBANK)', value: '004' },
  { label: 'กรุงไทย (KTB)', value: '006' },
  { label: 'กรุงศรีอยุธยา (BAY)', value: '025' },
  { label: 'เกียรตินาคิน (KKP)', value: '069' },
  { label: 'ออมสิน (GSB)', value: '030' },
  { label: 'ทหารไทยธนชาต', value: '011' },
  { label: 'กรุงเทพ', value: '002' },
  { label: 'ไอซีบีซี', value: '070' },
  { label: 'ไทยเครดิต', value: '071' },
  { label: 'ซิตี้แบงก์', value: '017' },
  { label: 'ซูมิโตโม มิตซุย', value: '018' },
  { label: 'สแตนดาร์ดชาร์เต', value: '020' },
  { label: 'ซีไอเอ็มบีไทย', value: '022' },
  { label: 'ยูโอบี', value: '024' },
  { label: 'เอชเอสบีซี', value: '031' },
  { label: 'มิซูโฮ', value: '039' },
  { label: 'ธกส.', value: '034' },
  { label: 'ธอส.', value: '033' },
  { label: 'แลนด์ แอนด์เฮ้าส์', value: '073' },
  { label: 'ทิสโก้', value: '067' },
  { label: 'อิสลาม', value: '066' }
]

export default {
  name: 'settingPayment',
  components: { PaymentRulesPanel },
  data () {
    return {
      isPageLoading: true,
      activeTabIndex: 0,
      ranges: [],
      bankLists: [],
      loading: false,
      saving: false,
      deleting: false,
      showFormModal: false,
      showDeleteModal: false,
      formMode: 'add', // 'add' | 'edit'
      form: {
        id: null,
        min_amount: null,
        payment_code: '',
        is_active: true
      },
      formErrors: {},
      deleteTarget: null,
      // จับคู่ payment กับธนาคารลูกค้า
      bankOptions: BANK_OPTIONS,
      bankPaymentMapping: {}, // { bankCode: paymentCode }
      defaultPaymentCode: '',
      savingMapping: false,
      // ปรับ min_deposit ของ payment
      showMinDepositModal: false,
      savingMinDeposit: false,
      minDepositForm: {
        code: '',
        name: '',
        min_deposit: null
      },
      // setting 125: true = filter ด้วยยอดฝาก (ON), false = filter ด้วยธนาคารลูกค้า (OFF) — เปิดได้แค่ทีละอัน
      depositRangesFilterOn: false,
      updatingFilterSetting: false
    }
  },
  async mounted () {
    try {
      await Promise.all([
        // this.fetchDepositRangesFilterStatus(),
        // this.fetchRanges(),
        this.fetchBankLists()
      ])
    } finally {
      this.isPageLoading = false
    }
  },
  watch: {
  },
  methods: {
    async fetchDepositRangesFilterStatus () {
      try {
        const data = await getDepositRangesFilterStatus()
        this.depositRangesFilterOn = !!data.setting_status
      } catch (_) {
        this.depositRangesFilterOn = false
      }
    },
    async updateDepositRangesFilter () {
      this.updatingFilterSetting = true
      const targetStatus = !this.depositRangesFilterOn
      try {
        const res = await updateDepositRangesFilterStatus(targetStatus)
        const hasSettingStatus = res && typeof res.setting_status === 'boolean'
        const effective = hasSettingStatus ? res.setting_status : targetStatus
        this.depositRangesFilterOn = !!effective

        if (res && res.status === true) {
          this.$vs.notify({
            time: 3000,
            color: effective ? 'success' : 'warning',
            position: 'top-right',
            icon: effective ? 'check_box' : 'error',
            title: effective ? 'เปิด' : 'ปิด',
            text: effective ? 'Filter payment ด้วยยอดฝาก' : 'Filter payment ด้วยธนาคารลูกค้า'
          })
        } else {
          this.showError(new Error(res && res.message ? res.message : 'อัปเดตไม่สำเร็จ'))
        }
      } catch (err) {
        await this.fetchDepositRangesFilterStatus()
        this.showError(err)
      } finally {
        this.updatingFilterSetting = false
      }
    },
    async fetchRanges () {
      this.loading = true
      try {
        const data = await getRanges()
        this.ranges = Array.isArray(data) ? data : (data.data || [])
      } catch (err) {
        this.showError(err)
      } finally {
        this.loading = false
      }
    },
    async fetchBankLists () {
      try {
        const data = await getBankLists({ type: 'payment' })
        this.bankLists = Array.isArray(data) ? data : []
        await this.fetchBankPaymentMapping()
      } catch (err) {
        this.showError(err)
      }
    },
    async fetchBankPaymentMapping () {
      try {
        const data = await getBankPaymentMapping()
        const raw = data.bankPaymentMapping && typeof data.bankPaymentMapping === 'object' ? data.bankPaymentMapping : {}
        this.bankPaymentMapping = {}
        this.bankOptions.forEach(b => {
          const val = raw[b.value] !== undefined && raw[b.value] !== null ? String(raw[b.value]) : ''
          this.$set(this.bankPaymentMapping, b.value, val)
        })
        this.defaultPaymentCode = data.defaultPaymentCode !== undefined && data.defaultPaymentCode !== null ? String(data.defaultPaymentCode) : ''
      } catch (err) {
        this.bankPaymentMapping = {}
        this.bankOptions.forEach(b => {
          this.$set(this.bankPaymentMapping, b.value, '')
        })
        this.defaultPaymentCode = ''
      }
    },
    async doSaveBankPaymentMapping () {
      this.savingMapping = true
      try {
        await saveBankPaymentMapping({
          bankPaymentMapping: { ...this.bankPaymentMapping },
          defaultPaymentCode: this.defaultPaymentCode
        })
        this.$vs.notify({
          color: 'success',
          title: 'บันทึกการจับคู่สำเร็จ',
          position: 'top-right'
        })
      } catch (err) {
        this.showError(err)
      } finally {
        this.savingMapping = false
      }
    },
    async updateDefaultPaymentInstant () {
      if (this.savingMapping) return
      this.savingMapping = true
      try {
        await saveBankPaymentMapping({
          bankPaymentMapping: { ...this.bankPaymentMapping },
          defaultPaymentCode: this.defaultPaymentCode
        })
        this.$vs.notify({
          color: 'success',
          title: 'บันทึก default payment สำเร็จ',
          position: 'top-right'
        })
      } catch (err) {
        this.showError(err)
      } finally {
        this.savingMapping = false
      }
    },
    async updateBankMappingInstant (row) {
      if (this.savingMapping) return
      this.savingMapping = true
      try {
        await saveBankPaymentMapping({
          bankPaymentMapping: { ...this.bankPaymentMapping },
          defaultPaymentCode: this.defaultPaymentCode
        })
        const selectedCode = this.bankPaymentMapping[row.value] || this.defaultPaymentCode || '-'
        this.$vs.notify({
          color: 'success',
          title: 'บันทึกการตั้งค่าสำเร็จ',
          text: `${row.label} -> ${selectedCode}`,
          position: 'top-right'
        })
      } catch (err) {
        this.showError(err)
      } finally {
        this.savingMapping = false
      }
    },
    async resetBankMapping (row) {
      this.$set(this.bankPaymentMapping, row.value, '')
      await this.updateBankMappingInstant(row)
    },
    setBankMappingToDefault (bankCode) {
      this.$set(this.bankPaymentMapping, bankCode, this.defaultPaymentCode || '')
    },
    openAddModal () {
      this.formMode = 'add'
      this.resetForm()
      this.form.is_active = true
      this.showFormModal = true
    },
    openEditModal (row) {
      this.formMode = 'edit'
      this.form = {
        id: row.id,
        min_amount: row.min_amount,
        payment_code: row.payment_code,
        is_active: !!row.is_active
      }
      this.formErrors = {}
      this.showFormModal = true
    },
    resetForm () {
      this.form = {
        id: null,
        min_amount: null,
        payment_code: '',
        is_active: true
      }
      this.formErrors = {}
    },
    validateForm () {
      this.formErrors = {}
      if (this.form.min_amount === null || this.form.min_amount === undefined || this.form.min_amount === '' || isNaN(this.form.min_amount)) {
        this.formErrors.min_amount = 'กรุณากรอก min_amount'
      } else if (this.form.min_amount < 0) {
        this.formErrors.min_amount = 'min_amount ต้องเป็นจำนวนบวก'
      }
      if (!this.form.payment_code || !String(this.form.payment_code).trim()) {
        this.formErrors.payment_code = 'กรุณากรอก payment_code'
      }
      return Object.keys(this.formErrors).length === 0
    },
    async submitForm () {
      if (!this.validateForm()) return
      this.saving = true
      try {
        if (this.formMode === 'add') {
          const res = await createRange({
            min_amount: Number(this.form.min_amount),
            payment_code: String(this.form.payment_code).trim(),
            is_active: this.form.is_active
          })
          if (res.data) this.ranges = res.data
          this.showFormModal = false
          this.$vs.notify({ color: 'success', title: 'Add payment rangeสำเร็จ', text: '', position: 'top-right' })
        } else {
          const res = await updateRange(this.form.id, {
            min_amount: Number(this.form.min_amount),
            payment_code: String(this.form.payment_code).trim(),
            is_active: this.form.is_active
          })
          if (res.data) this.ranges = res.data
          this.showFormModal = false
          this.$vs.notify({ color: 'success', title: 'แก้ไข rangeสำเร็จ', text: '', position: 'top-right' })
        }
      } catch (err) {
        this.showError(err)
      } finally {
        this.saving = false
      }
    },
    async toggleActive (row) {
      const newActive = !row.is_active
      try {
        const res = await updateRange(row.id, { is_active: newActive })
        if (res.data) this.ranges = res.data
        this.$vs.notify({
          color: 'success',
          title: newActive ? 'เปิดใช้งานสำเร็จ' : 'ปิดใช้งานสำเร็จ',
          position: 'top-right'
        })
      } catch (err) {
        this.showError(err)
        row.is_active = !newActive // revert on error
      }
    },
    openDeleteConfirm (row) {
      this.deleteTarget = row
      this.showDeleteModal = true
    },
    async confirmDelete () {
      if (!this.deleteTarget) return
      this.deleting = true
      try {
        const res = await deleteRange(this.deleteTarget.id)
        if (res.data) this.ranges = res.data
        this.showDeleteModal = false
        this.deleteTarget = null
        this.$vs.notify({ color: 'success', title: 'ลบ rangeสำเร็จ', position: 'top-right' })
      } catch (err) {
        this.showError(err)
      } finally {
        this.deleting = false
      }
    },
    showError (err) {
      const msg = (err.response && err.response.data && err.response.data.message) || err.message || 'เกิดข้อผิดพลาด'
      this.$vs.notify({
        color: 'danger',
        title: 'เกิดข้อผิดพลาด',
        text: msg,
        position: 'top-right',
        time: 5000
      })
    },
    formatNumber (val) {
      if (val === null || val === undefined) return '-'
      return Number(val).toLocaleString('th-TH', { minimumFractionDigits: 2 })
    },
    getPaymentLabelByCode (code) {
      if (!code) return '-'
      const found = this.bankLists.find(item => item.code === code)
      return found ? (found.name_th || found.name_en || found.code) : code
    },
    getActiveBankMappingsText () {
      const pairs = []
      this.bankOptions.forEach((bank) => {
        const code = this.bankPaymentMapping[bank.value]
        if (code) pairs.push(`${bank.label} -> ${this.getPaymentLabelByCode(code)}`)
      })
      return pairs.join('\n')
    },
    getActiveRangeSummaryText () {
      const activeRanges = (this.ranges || []).filter(item => item && item.is_active)
      if (activeRanges.length === 0) return ''
      const lines = activeRanges.map((item) => {
        const min = this.formatNumber(item.min_amount)
        const max = this.formatNumber(item.max_amount)
        const payment = this.getPaymentLabelByCode(item.payment_code)
        return `${min} - ${max} -> ${payment}`
      })
      return lines.join('\n')
    },
    openMinDepositModal (item) {
      const name = (item && (item.name_th || item.name_en)) ? String(item.name_th || item.name_en) : ''
      this.minDepositForm = {
        code: item && item.code ? String(item.code) : '',
        name,
        min_deposit: item && item.min_deposit !== undefined && item.min_deposit !== null ? Number(item.min_deposit) : 0
      }
      this.showMinDepositModal = true
    },
    resetMinDepositForm () {
      this.minDepositForm = { code: '', name: '', min_deposit: null }
    },
    async submitMinDeposit () {
      if (!this.minDepositForm.code) return
      const minDeposit = Number(this.minDepositForm.min_deposit)
      if (isNaN(minDeposit) || minDeposit < 0) {
        this.showError(new Error('min_deposit ต้องเป็นจำนวน 0 ขึ้นไป'))
        return
      }

      this.savingMinDeposit = true
      try {
        await updatePaymentMinDeposit(this.minDepositForm.code, minDeposit)
        await this.fetchBankLists()
        this.showMinDepositModal = false
        this.$vs.notify({ color: 'success', title: 'บันทึกยอดฝากขั้นต่ำสำเร็จ', position: 'top-right' })
      } catch (err) {
        this.showError(err)
      } finally {
        this.savingMinDeposit = false
      }
    }
  }
}
</script>
